/**
 * 角色的展示层定义。**唯一真相源** —— 任何地方要显示角色名或颜色都从这里取，
 * 不要在组件里各写一份 switch。后端的 member_role 枚举是它的对应物。
 */
export const ROLES = ['admin', 'finance', 'member'] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  admin: '管理员',
  finance: '财务',
  member: '成员',
};

export const ROLE_COLOR: Record<Role, string> = {
  admin: 'red',
  finance: 'gold',
  member: 'default',
};

/** 下拉选项。顺序固定，别按对象 key 顺序临时生成。 */
export const ROLE_OPTIONS = ROLES.map((value) => ({ value, label: ROLE_LABEL[value] }));

/**
 * 「打款链路 + 成员名单」这道闸 —— 与服务端 assertAdminOrFinance 是同一条判定。
 *
 * 🔴 member 只负责把收款信息收上来，不碰钱：打款申请、打款记录、成员名单它都看不到，
 * 直接访问 URL 也会被路由挡下，直接打接口会被服务端 403。
 * ⚠️ 这里是**界面判定**，不是权限本身；真正的把关永远在服务端。
 */
export function isAdminOrFinance(roles: Role[]): boolean {
  return roles.includes('admin') || roles.includes('finance');
}

export type MemberStatus = 'active' | 'disabled';

/**
 * 角色集合的规范形式：去重 + 按 ROLES 的固定顺序排列。
 * 与服务端 members.service.ts 的 normalizeRoles 同一套规则 —— 两边都规范化，
 * 「角色有没有变」才能靠逐项比较判断。
 */
export function normalizeRoles(roles: Role[]): Role[] {
  const set = new Set(roles);
  return ROLES.filter((r) => set.has(r));
}

/** 两个角色集合是否等价。调用前不必自己排序。 */
export function sameRoles(a: Role[], b: Role[]): boolean {
  const x = normalizeRoles(a);
  const y = normalizeRoles(b);
  return x.length === y.length && x.every((r, i) => r === y[i]);
}

/** 展示用的一行文案，如「管理员、财务」。 */
export function rolesLabel(roles: Role[]): string {
  return normalizeRoles(roles).map((r) => ROLE_LABEL[r]).join('、');
}
